import { useState } from "react";
import { Link } from "react-router-dom";
import { Send, Mail, AlertCircle, Loader2 } from "lucide-react";
import { useSubmitAccessRequest, useTrustConfig } from "@/api/client";

interface FormData {
  name: string;
  email: string;
  company: string;
  purpose: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  company?: string;
  purpose?: string;
}

function validateEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function validateForm(data: FormData): FormErrors {
  const errors: FormErrors = {};

  if (!data.name.trim()) {
    errors.name = "Name is required";
  }

  if (!data.email.trim()) {
    errors.email = "Email is required";
  } else if (!validateEmail(data.email.trim())) {
    errors.email = "Please enter a valid email address";
  }

  if (!data.company.trim()) {
    errors.company = "Company name is required";
  }

  if (!data.purpose.trim()) {
    errors.purpose = "Please describe the purpose of your request";
  } else if (data.purpose.trim().length < 10) {
    errors.purpose = "Please provide more detail (at least 10 characters)";
  }

  return errors;
}

function InputField({
  id,
  label,
  type = "text",
  value,
  onChange,
  error,
  placeholder,
}: {
  id: string;
  label: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  placeholder?: string;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-sm font-medium text-gray-700"
      >
        {label}
        <span className="text-red-500"> *</span>
      </label>
      <input
        id={id}
        name={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 ${
          error
            ? "border-red-300 focus:border-red-400"
            : "border-gray-300 focus:border-brand-primary"
        }`}
      />
      {error && (
        <p className="mt-1 text-xs text-red-500">{error}</p>
      )}
    </div>
  );
}

export default function RequestPage() {
  const [form, setForm] = useState<FormData>({
    name: "",
    email: "",
    company: "",
    purpose: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);

  const mutation = useSubmitAccessRequest();
  const { data: trust } = useTrustConfig();
  const companyName = trust?.tenant.company_name ?? "our team";

  function updateField(field: keyof FormData) {
    return (value: string) => {
      setForm((prev) => ({ ...prev, [field]: value }));
      // Clear field error on change
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    };
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const validationErrors = validateForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});

    mutation.mutate(
      {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        purpose: form.purpose.trim(),
      },
      {
        onSuccess: () => {
          setSubmitted(true);
        },
      }
    );
  }

  // Success state - "Check your email" confirmation
  if (submitted) {
    return (
      <div className="mx-auto max-w-md">
        <div className="flex flex-col items-center rounded-lg border border-gray-200 bg-white px-6 py-12 text-center shadow-sm">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-primary/10">
            <Mail className="h-7 w-7 text-brand-primary" />
          </div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">
            Check your email
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            Your request has been sent to {companyName}.
          </p>
          <p className="mt-3 text-sm text-gray-500">
            A verification link has been sent to:
          </p>
          <p className="mt-1 text-sm font-semibold text-gray-900">
            {form.email}
          </p>
          <p className="mt-4 text-xs text-gray-400">
            Please check your inbox and click the link to continue. If you
            don't see the email, check your spam folder.
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
          >
            Back to Trust Center
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Request Access</h1>
        <p className="mt-1 text-sm text-gray-500">
          Submit a request to access restricted security documents or schedule a
          security review. Our team typically responds within 1-2 business days.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="card space-y-5 p-6" noValidate>
        <InputField
          id="name"
          label="Full Name"
          value={form.name}
          onChange={updateField("name")}
          error={errors.name}
          placeholder="Jane Smith"
        />

        <InputField
          id="email"
          label="Work Email"
          type="email"
          value={form.email}
          onChange={updateField("email")}
          error={errors.email}
          placeholder="jane@company.com"
        />

        <InputField
          id="company"
          label="Company"
          value={form.company}
          onChange={updateField("company")}
          error={errors.company}
          placeholder="Acme Inc."
        />

        <div>
          <label
            htmlFor="purpose"
            className="block text-sm font-medium text-gray-700"
          >
            Purpose
            <span className="text-red-500"> *</span>
          </label>
          <textarea
            id="purpose"
            name="purpose"
            rows={4}
            value={form.purpose}
            onChange={(e) => updateField("purpose")(e.target.value)}
            placeholder="Describe why you need access to our security documentation..."
            className={`mt-1 block w-full resize-none rounded-md border px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 ${
              errors.purpose
                ? "border-red-300 focus:border-red-400"
                : "border-gray-300 focus:border-brand-primary"
            }`}
          />
          {errors.purpose && (
            <p className="mt-1 text-xs text-red-500">{errors.purpose}</p>
          )}
        </div>

        {/* Mutation error */}
        {mutation.isError && (
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />
            <p className="text-sm text-red-700">
              {mutation.error?.message ||
                "Something went wrong. Please try again."}
            </p>
          </div>
        )}

        <button
          type="submit"
          disabled={mutation.isPending}
          className="flex w-full items-center justify-center gap-2 rounded-md bg-brand-primary px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="h-4 w-4" />
              Submit Request
            </>
          )}
        </button>
      </form>

      <p className="text-center text-xs text-gray-400">
        Your information will only be used to process this access request.
      </p>
    </div>
  );
}
